import ModuleInvoice from "@/views/components/module-invoice"
import ModuleEInvoiceDetail from "@/views/components/module-einvoice-detail"
import EInvoiceDetail from "../add-e-invoice/e-invoice-detail"
import SeparateRowPreviewModal from "../add-e-invoice/separate-row-preview-modal.vue"
import { createDraftEInvoice, createEInvoice, publishEInvoice,
        previewTempPdfEInvoice, createInternalEinvoice } from "@/api/action-defines/e-invoice-management"
import { detailSiteApi, getSitePartner } from "@/api/action-defines/site-management"
import dayjs from "dayjs"
import { v4 as uuidv4 } from 'uuid'
import { mapState, mapGetters } from "vuex"
import { INVOICE_ACTION } from "@/@core/data/enum/eInvoiceActionType"
import { PartnerCode } from "@/@core/data/enum/partnerCode"
import { EInvoiceActionCodes } from '@/@core/data/enum/menuCode'

/**
 * Màn "Xuất hóa đơn tùy chỉnh" — điểm đến của luồng phần mềm khác (PMS) mở sang:
 *   /e-invoice/custom-export?reservationIds=…&hotelId=…&siteId=…&reservationRoomIds=…&infoReservationId=…
 *
 * Trước đây luồng này đi qua màn danh sách hóa đơn (EInvoice.vue) rồi giả lập click mở sidebar AddEInvoice,
 * nên phải nạp cả search-bar, danh sách, thống kê… của màn danh sách trước khi bắt đầu tự search & fill.
 * Màn này chỉ dựng đúng 3 khối cần cho việc tạo hóa đơn từ module (ModuleInvoice → EInvoiceDetail →
 * ModuleEInvoiceDetail) và giữ nguyên cơ chế tự search / tự chọn / tự fill sẵn có trong các component đó
 * (chúng đọc cờ sessionStorage 'isRedirected' + tham số trên URL).
 *
 * Chỉ hỗ trợ hành động TẠO MỚI (ADD_INVOICE); điều chỉnh / thay thế vẫn dùng AddEInvoice như cũ.
 */
export default {
  name: 'CustomExportEInvoice',
  components: {
    ModuleInvoice,
    ModuleEInvoiceDetail,
    EInvoiceDetail,
    SeparateRowPreviewModal,
  },

  data() {
    return {
      // Chỉ dựng giao diện (và để component con gọi API) sau khi tham số redirect + site + quyền đã hợp lệ
      isReady: false,
      isModulePaneOpen: true,
      // Luồng redirect hợp lệ (đủ tham số + đúng site): các component con sẽ tự search & fill
      isAutoFillFlow: false,
      // Đã tự thu cột module sau khi fill xong (chỉ làm 1 lần)
      hasAutoCollapsedModulePane: false,
      invoiceAction: INVOICE_ACTION,
      partnerInfo: {},
      tempInvoiceData: null,
      tempInvoiceItems: [],
      pendingApiCall: null,
    }
  },

  created() {
    this.clearStore();

    // Phải quyết định cờ 'isRedirected' NGAY TRONG created() (trước khi render lần đầu): ModuleInvoice,
    // ModuleInvoiceDetail và EInvoiceDetail đọc sessionStorage trong created()/mounted() của chúng để biết có
    // tự search & fill hay không. Đặt trễ hơn là các component con đã chạy qua bước kiểm tra.
    const redirect = this.readRedirectParams();

    if (redirect.hasParams && !this.isSameSite(redirect.siteId, this.$ls_CurrentSiteID)) {
      // Chống SAI SITE: PMS gửi kèm siteId của nhà cung cấp người dùng đã chọn bên PMS. Nếu phiên ezInvoice
      // đang ở site khác thì hóa đơn sẽ phát hành sai người bán mà không có dấu hiệu nào ⇒ báo lỗi, về danh sách.
      sessionStorage.setItem('isRedirected', 'false');
      this.$toast.error(this.translateOrDefault(
        'module_invoice_wrong_site',
        'Bạn đang đăng nhập ezInvoice ở site khác với site đã chọn bên PMS. Vui lòng chuyển đúng site rồi thử lại.',
      ));
      this.$router.replace({ name: 'invoice-management' });
      return;
    }

    // Cùng điều kiện với nút "Tạo hóa đơn" ở màn danh sách. Chỉ chặn khi đã có dữ liệu phân quyền,
    // tránh chặn oan lúc tài nguyên chưa nạp xong.
    if (this.isMenusRolesLoaded && !this.isCanCreateEInvoice()) {
      sessionStorage.setItem('isRedirected', 'false');
      this.$toast.error(this.translateOrDefault(
        'module_invoice_no_permission_create',
        'Bạn không có quyền tạo hóa đơn.',
      ));
      this.$router.replace({ name: 'invoice-management' });
      return;
    }

    sessionStorage.setItem('isRedirected', redirect.hasParams ? 'true' : 'false');
    this.$store.commit('eInvoiceManagement/setAutoSelectAfterSearch', redirect.hasParams);
    this.isAutoFillFlow = redirect.hasParams;

    this.isReady = true;
    this.getPartnerInfo();
  },

  mounted() {
    this.$nextTick(() => this.initPanes());
  },

  beforeDestroy() {
    this.clearStore();
    // Không để cờ tự-fill rơi sang màn khác (vd mở sidebar Tạo hóa đơn ở màn danh sách sẽ tự chọn hết dòng)
    sessionStorage.setItem('isRedirected', 'false');
    this.$store.commit('eInvoiceManagement/setAutoSelectAfterSearch', false);
  },

  methods: {
    // ---------------------------------------------------------------------------------------------
    // Khởi tạo màn / tham số redirect
    // ---------------------------------------------------------------------------------------------
    readRedirectParams() {
      const query = this.$route.query || {};
      const reservationIds = query.reservationIds;
      const hotelId = query.hotelId;

      return {
        reservationIds,
        hotelId,
        siteId: query.siteId,
        hasParams: !!(reservationIds && hotelId),
      };
    },

    // So sánh site: id đi qua URL nên phải bỏ qua hoa/thường và khoảng trắng.
    isSameSite(a, b) {
      if (!a || !b) return true;

      return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
    },

    // i18n của màn này nạp từ backend: khóa chưa đăng ký sẽ hiện ra chính chuỗi khóa, nên có câu mặc định.
    translateOrDefault(key, fallback) {
      const text = this.$t(key);

      return !text || text === key ? fallback : text;
    },

    initPanes() {
      const moduleInvoice = this.$refs.moduleInvoice;
      if (moduleInvoice) {
        // Sidebar cũ mở panel module bằng click nút "<<"; ở màn riêng panel luôn mở sẵn
        moduleInvoice.isOpen = true;
        // Panel có nút đóng riêng bên trong ⇒ đồng bộ ngược để nút toggle ở header hiển thị đúng
        this.$watch(() => moduleInvoice.isOpen, (val) => {
          this.isModulePaneOpen = !!val;
        }, { immediate: true });
      }

      if (this.$refs.EInvoiceDetail) {
        // Mở sẵn form thông tin hóa đơn để người dùng thấy dữ liệu khách được fill từ module
        this.$refs.EInvoiceDetail.isOpen = true;
      }
    },

    toggleModulePane() {
      const moduleInvoice = this.$refs.moduleInvoice;
      if (!moduleInvoice) return;

      moduleInvoice.isOpen = !moduleInvoice.isOpen;
    },

    // Rời màn bằng replace (không push): URL của màn này còn mang reservationIds/hotelId, nếu để lại trong
    // history thì bấm Back sẽ dựng lại màn và chạy lại toàn bộ tự search / tự thêm dòng ⇒ dễ phát hành trùng.
    closeCustomExport() {
      this.$router.replace({ name: 'invoice-management' });
    },

    // Sau khi tạo / lưu nháp thành công: về danh sách và yêu cầu nạp lại (giống AddEInvoice)
    goToInvoiceListAfterSuccess() {
      this.$toast.success(this.$t('success'));
      this.$router.replace({
        name: 'invoice-management',
        query: { reload: true }
      });
    },

    async getPartnerInfo() {
      const res = await getSitePartner();
      this.partnerInfo = res.Data;
    },

    clearStore() {
      this.$store.commit('eInvoiceManagement/UPDATE_TABLE_ITEMS', []);
      this.$store.commit('eInvoiceManagement/UPDATE_ADDED_GROUPS', []);
      this.$store.commit('eInvoiceManagement/UPDATE_ADDED_INVOICES', []);
      this.$store.commit('eInvoiceManagement/UPDATE_FILLED_ITEMS', []);
      this.$store.commit('eInvoiceManagement/SELECT_ALL', false);
    },

    // ---------------------------------------------------------------------------------------------
    // Phân quyền / nhà cung cấp
    // ---------------------------------------------------------------------------------------------
    isCanCreateEInvoice() {
      return this.$lsAllowOperation(EInvoiceActionCodes.ADD);
    },
    isCanAddEInvoice() {
      return this.$lsAllowOperation(EInvoiceActionCodes.RELEASE);
    },
    isCanDraftEInvoice() {
      const { VIETTEL, SUN, CYBER_BILL, VNPT, VNPT_V5, EASY } = PartnerCode;
      return this.$lsAllowOperation(EInvoiceActionCodes.DRAFT)
        && [VIETTEL, SUN, CYBER_BILL, VNPT, VNPT_V5, EASY].includes(this.partnerInfo.PartnerCode);
    },
    isCanPreviewTempEInvoice() {
      const { VIETTEL, MISA, FAST, EASY } = PartnerCode;
      return [VIETTEL, MISA, FAST, EASY].includes(this.partnerInfo.PartnerCode);
    },

    // ---------------------------------------------------------------------------------------------
    // Dựng payload (cùng luật với AddEInvoice — luồng ADD_INVOICE)
    // ---------------------------------------------------------------------------------------------
    // Thông tin hóa đơn + người mua đang nhập trên form
    collectDetailData() {
      return {
        EinvoiceDate: '',
        InvoiceNo: '',
        Serial: null,
        FormInvoice: null,
        CustomerName: null,
        CustomerPhone: null,
        CustomerEmail: null,
        CompanyName: null,
        CustomerAddress: null,
        CompanyAddress: null,
        CustomerTax: null,
        BankName: null,
        BankAccount: null,
        PaymentMethodCode: '',
        Cccd: null,
        Passport: null,
        TaxReduction43Amount: null,
        ...this.$refs.EInvoiceDetail.getDetailData()
      };
    },

    // Helper method to get items detail with grouping information
    getItemsDetailWithGroupInfo() {
      const itemsDetailResult = this.$refs.moduleEInvoiceDetail.getItemsDetail();
      return {
        items: itemsDetailResult.items,
        isGrouped: itemsDetailResult.isGrouped
      };
    },

    // Chuẩn hóa tên trường dòng hàng theo hợp đồng API (mutate tại chỗ, giống AddEInvoice)
    normalizeItemsForPayload(items) {
      items.forEach(item => {
        item.SubAmount = item.BeforeTaxAmount;
        // GIỮ khóa dòng gốc: ReferenceItemId là id giao dịch của hệ nguồn (ezOne.PMS / module) — thứ duy nhất
        // để đồng bộ hóa đơn ngược về hệ đó. Dòng mới chưa có ReferenceItemId thì rơi về ItemId.
        item.ReferenceItemId = item.ReferenceItemId ? item.ReferenceItemId : item.ItemId;
        item.TaxRate = item.Tax;
        item.ServiceRate = item.Charges;
        item.ServicesCharge = item.ChargesAmount;
        item.DiscountPercent = 0;
        item.AfterTaxAmount = item.TotalAmount;
      });
      return items;
    },

    /**
     * Soi danh sách dòng hàng trước khi lưu / phát hành.
     *
     * `onlyExternalService` = hóa đơn KHÔNG có dòng nào đến từ hệ thống nguồn, tức chỉ toàn dòng
     * "Thêm dịch vụ ngoài" người dùng tự nhập ⇒ chặn.
     *
     * Dấu hiệu "dòng có nguồn" KHÔNG chỉ là GroupItemId: hóa đơn ezOne.PMS xuất thẳng gửi GroupItemId = null
     * nhưng vẫn mang ReservationId/ReservationRoomId. ⛔ Không dùng ReferenceItemId làm dấu hiệu.
     */
    inspectInvoiceLines(lines) {
      const list = Array.isArray(lines) ? lines : ((lines && lines.items) || []);
      let onlyExternalService = true;
      let isItemNameNull = false;

      list.forEach(elm => {
        if (elm.ItemName === "" || elm.ItemName === null || elm.ItemName === undefined) {
          isItemNameNull = true;
        }
        const isModuleSourced = elm.GroupItemId != null
          || elm.ReservationRoomId != null
          || elm.ReservationId != null;
        if (isModuleSourced) {
          onlyExternalService = false;
        }
      });

      return { onlyExternalService, isItemNameNull };
    },

    // Ngày hoá đơn gửi xuống backend giữ đúng giờ local (không convert sang UTC).
    // Người dùng chọn ngày nào thì gửi đúng ngày đó, không chọn thì lấy date time hiện tại.
    getEinvoiceDateToSend(einvoiceDate) {
      const SEND_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

      if (!einvoiceDate) {
        return dayjs().format(SEND_FORMAT);
      }

      // flatpickr (dateFormat 'd/m/Y') trả về chuỗi 'DD/MM/YYYY',
      // còn giá trị khởi tạo trên form là Date nên phải tách 2 trường hợp
      if (typeof einvoiceDate === 'string') {
        const matched = einvoiceDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (matched) {
          const [, day, month, year] = matched;
          return dayjs(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`).format(SEND_FORMAT);
        }
      }

      const parsed = dayjs(einvoiceDate);
      return parsed.isValid() ? parsed.format(SEND_FORMAT) : dayjs().format(SEND_FORMAT);
    },

    // Payload cho lưu nháp / nháp nội bộ / kiểm tra trước phát hành: lấy items theo model gốc (getItemsDetail)
    buildDraftBodyData(detailData) {
      const itemsDetailInfo = this.getItemsDetailWithGroupInfo();
      const itemsDetail = this.normalizeItemsForPayload(itemsDetailInfo.items);
      const einvoiceData = this.$refs.moduleEInvoiceDetail.getTotalData();

      const invoiceDetail = {
        ...detailData,
        ezCloudClientID: uuidv4(),
        EinvoiceDate: this.getEinvoiceDateToSend(detailData.EinvoiceDate),
        FormNo: detailData.FormInvoice,
        Serial: detailData.Serial,
        CurrencyCode: 'VND',
        ExchangeRate: 0,
        SubAmount: 0,
        DiscountPercent: 0,
        DiscountAmount: einvoiceData.DiscountAmount,
        ServiceRate: 0,
        ServicesCharge: einvoiceData.ServicesCharge,
        BeforeTaxAmount: einvoiceData.BeforeTaxAmount,
        TaxRate: 0,
        TaxAmount: einvoiceData.TaxAmount,
        AfterTaxAmount: einvoiceData.AfterTaxAmount,
        StatusSign: false,
        HotelId: '0'
      };

      return {
        ...invoiceDetail,
        EInvoiceDetails: itemsDetail,
        IsGrouped: itemsDetailInfo.isGrouped // Include grouping flag
      };
    },

    // Payload THẬT cho tạo mới / xem trước PDF nháp: lấy items theo đúng dữ liệu đang hiển thị cho khách
    // (đã áp cấu hình hóa đơn), không lấy model gốc được truyền sang.
    prepareInvoiceData(detailData) {
      const itemsDetailResult = this.$refs.moduleEInvoiceDetail.getDisplayedItemsDetail();
      const itemsDetailInfo = {
        items: itemsDetailResult.items,
        isGrouped: itemsDetailResult.isGrouped
      };
      const itemsDetail = itemsDetailInfo.items;

      const eInvoiceDate = this.getEinvoiceDateToSend(detailData.EinvoiceDate);

      // Process items
      const targetItems = itemsDetail.items ?? itemsDetail;

      if (targetItems) {
        this.normalizeItemsForPayload(targetItems);
      }

      // Tổng tiền cộng trên chính items đang hiển thị để khớp với màn hình khách xem
      const einvoiceData = this.$refs.moduleEInvoiceDetail.getDisplayedTotalData(itemsDetail);

      const isBHInvoice = this.$ls_EInvoiceConfig?.InvoiceType === 'BH_INVOICE';

      // Create invoice detail object
      const invoiceDetail = {
        ...detailData,
        ezCloudClientID: uuidv4(),
        EinvoiceDate: eInvoiceDate,
        FormNo: detailData.FormInvoice,
        Serial: detailData.Serial,
        CurrencyCode: 'VND',
        ExchangeRate: 0,
        SubAmount: einvoiceData.SubAmount,
        DiscountPercent: 0,
        DiscountAmount: einvoiceData.DiscountAmount,
        ServiceRate: einvoiceData.ServiceRate,
        ServicesCharge: einvoiceData.ServicesCharge,
        BeforeTaxAmount: einvoiceData.BeforeTaxAmount,
        TaxRate: 0,
        TaxAmount: einvoiceData.TaxAmount,
        AfterTaxAmount: einvoiceData.AfterTaxAmount,
        StatusSign: false,
        HotelId: '0',
        IsTaxReduction43: isBHInvoice
      };

      // Prepare and return the final body data
      return {
        invoiceDetail,
        itemsDetailInfo,
        bodyData: {
          ...invoiceDetail,
          EInvoiceDetails: itemsDetailInfo.items,
          IsGrouped: itemsDetailInfo.isGrouped // Include grouping flag
        }
      };
    },

    // Kiểm tra bắt buộc trên form thông tin hóa đơn; trả về true nếu hợp lệ (đã toast lỗi nếu không)
    async validateDetailForm() {
      this.$refs.EInvoiceDetail.isOpen = true;
      const res = await this.$refs.EInvoiceDetail.validateFormReturnInfor();

      if (res.length > 0) {
        let text = this.$t('add_einvoice_pls_insert_required_input') + ': ';
        res.forEach(element => {
          text += this.$t(element) + ', ';
        });
        text = text.slice(0, -2) + '.';
        this.$toast.error(text);
        return false;
      }
      return true;
    },

    // Kiểm tra dòng hàng; trả về true nếu hợp lệ (đã toast lỗi nếu không)
    validateInvoiceLines(lines) {
      const { onlyExternalService, isItemNameNull } = this.inspectInvoiceLines(lines);

      if (onlyExternalService) {
        this.$toast.error(this.$t('add_einvoice_unable_create_invoice_without_services_external_providers_pls_check_and_try_again'));
        return false;
      }

      if (isItemNameNull) {
        this.$toast.error(this.$t('item_name_cant_null'));
        return false;
      }

      return true;
    },

    // ---------------------------------------------------------------------------------------------
    // Hành động
    // ---------------------------------------------------------------------------------------------
    async handleSaveDraft() {
      const { VNPT } = PartnerCode;
      const partnerNotHaveCreateDraftEInvoice = [VNPT];

      if (partnerNotHaveCreateDraftEInvoice.includes(this.partnerInfo.PartnerCode)) {
        return;
      }

      const detailData = this.collectDetailData();
      const bodyData = this.buildDraftBodyData(detailData);

      if (!await this.validateDetailForm()) return;
      if (!this.validateInvoiceLines(bodyData.EInvoiceDetails)) return;

      this.$refs.EInvoiceDetail.isOpen = true;

      await createDraftEInvoice({ BodyData: bodyData }).then(res => {
        if (res.StatusCode === '200') {
          this.goToInvoiceListAfterSuccess();
        }
      });
    },

    async createInternalEinvoice() {
      const detailData = this.collectDetailData();
      const bodyData = this.buildDraftBodyData(detailData);

      if (!await this.validateDetailForm()) return;
      if (!this.validateInvoiceLines(bodyData.EInvoiceDetails)) return;

      this.$refs.EInvoiceDetail.isOpen = true;

      await createInternalEinvoice({ BodyData: bodyData }).then(res => {
        if (res.StatusCode === '200') {
          this.goToInvoiceListAfterSuccess();
        }
      });
    },

    async handleReleaseInvoice() {
      this.$refs.confirmAddEinvoice.isOpen = false;

      const detailData = this.collectDetailData();
      const bodyData = this.buildDraftBodyData(detailData);

      if (!await this.validateDetailForm()) return;

      const QueryParameters = {
        siteId: this.$ls_CurrentSiteID,
      };

      let connectezInvoice = true;
      await detailSiteApi({ QueryParameters }).then((res) => {
        if (res.IsSuccess) {
          if (res.Data.Status.toUpperCase() !== 'ACTIVE') {
            this.$toast.error(this.$t('add_einvoice_pls_check_connect_config_ezInvoice'));
            connectezInvoice = false;
          }
        }
      });

      if (!connectezInvoice) return;

      if (bodyData.EInvoiceDetails.length <= 0) {
        this.$toast.error(this.$t('add_einvoice_pls_add_service'));
        return;
      }

      if (!this.validateInvoiceLines(bodyData.EInvoiceDetails)) return;

      this.$refs.confirmAddEinvoice.isOpen = true;
    },

    async confirmAddEinvoiceFunc() {
      this.$refs.confirmAddEinvoice.isOpen = false;

      const detailData = this.collectDetailData();

      // Prepare invoice data with items detail and grouping info
      const { bodyData, itemsDetailInfo } = this.prepareInvoiceData(detailData);
      const { items: itemsDetail } = itemsDetailInfo;

      // Extract partner codes from PartnerCode enum
      const { VNPT, VNPT_V5, SUN, VIETTEL, MISA, EASY, CMC, MINVOICE, CYBER_BILL } = PartnerCode;
      const partnerHavePublishEInvoice = [VNPT, VNPT_V5, SUN, VIETTEL, MISA, EASY];
      const parnerHaveCreateEInvoice = [CMC, MINVOICE, CYBER_BILL];

      let apiCall = null;
      if (parnerHaveCreateEInvoice.includes(this.partnerInfo.PartnerCode)) {
        apiCall = () => createEInvoice({ BodyData: bodyData });
      } else if (partnerHavePublishEInvoice.includes(this.partnerInfo.PartnerCode)) {
        apiCall = () => publishEInvoice({ BodyData: bodyData });
      }

      if (!apiCall) return;

      const submit = async () => {
        const res = await apiCall();
        if (res.StatusCode === '200') {
          this.goToInvoiceListAfterSuccess();
        }
        return res;
      };

      // Check if using SEPARATE_ROW charge option: xem trước dòng phí tách riêng rồi mới gửi
      if (this.hasSeparateRowChargeOption()) {
        this.showSeparateRowPreview(bodyData, itemsDetail, submit);
      } else {
        await submit();
      }
    },

    async confirmPreviewTempEinvoiceFunc() {
      const detailData = this.collectDetailData();

      // Prepare invoice data with items detail and grouping info
      const { bodyData } = this.prepareInvoiceData(detailData);

      const { VIETTEL, MISA, FAST, EASY } = PartnerCode;
      const partnerHavePreviewTempPdfEInvoice = [VIETTEL, MISA, FAST, EASY];

      if (!partnerHavePreviewTempPdfEInvoice.includes(this.partnerInfo.PartnerCode)) return;

      await previewTempPdfEInvoice({ BodyData: bodyData }).then(res => {
        if (res.StatusCode === '200') {
          this.$toast.success(this.$t('success'));
          if (res.Data.Type === 1) {
            const base64PDF = res.Data.InvoicePDF;
            const byteCharacters = atob(base64PDF);
            const byteNumbers = Array.from(byteCharacters, c => c.charCodeAt(0));
            const byteArray = new Uint8Array(byteNumbers);

            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl);
          }

          if (res.Data.Type === 2) {
            const pdfUrl = res.Data.InvoicePDF; // link trả về từ API
            window.open(pdfUrl, '_blank');
          }
        }
      });
    },

    // ---------------------------------------------------------------------------------------------
    // SEPARATE_ROW preview
    // ---------------------------------------------------------------------------------------------
    // Helper method to check if the invoice has SEPARATE_ROW charge option
    hasSeparateRowChargeOption() {
      return this.$ls_EInvoiceConfig?.ChargeOption === 'SEPARATE_ROW';
    },

    // Method to show preview modal for SEPARATE_ROW
    showSeparateRowPreview(invoiceData, invoiceItems, apiCallFunc) {
      this.tempInvoiceData = invoiceData;
      this.tempInvoiceItems = invoiceItems;
      this.pendingApiCall = apiCallFunc;

      this.$nextTick(() => {
        this.$refs.separateRowPreviewModal.showModal(invoiceData, invoiceItems);
      });
    },

    // Method to handle confirmation from the SEPARATE_ROW preview modal
    async handleSeparateRowPreviewConfirm() {
      if (!this.pendingApiCall || !this.tempInvoiceData) return;

      try {
        // Call the API function that was pending
        const result = await this.pendingApiCall();
        this.resetSeparateRowPreview();
        return result;
      } catch (error) {
        console.error('Error in handleSeparateRowPreviewConfirm:', error);
        this.$toast.error('Đã xảy ra lỗi khi xử lý hóa đơn');
        this.resetSeparateRowPreview();
        throw error;
      }
    },

    // Method to handle cancellation from the SEPARATE_ROW preview modal
    handleSeparateRowPreviewCancel() {
      this.resetSeparateRowPreview();
    },

    resetSeparateRowPreview() {
      this.tempInvoiceData = null;
      this.tempInvoiceItems = [];
      this.pendingApiCall = null;
    },
  },

  computed: {
    ...mapState({
      $ls_Items: (state) => state.eInvoiceManagement.items,
      $ls_EInvoiceConfig: (state) => state.eInvoiceManagement.eInvoiceConfig,
      $ls_MenusRoles: (state) => state.app.resources.MenusRoles,
    }),
    ...mapGetters({
      $ls_CurrentSiteID: 'auth/currentSiteID',
      $lsAllowOperation: 'app/allowOperation',
    }),
    isMenusRolesLoaded() {
      return (this.$ls_MenusRoles || []).length > 0;
    },
  },

  watch: {
    // Luồng redirect: khi ModuleInvoiceDetail đã tự thêm dòng vào hóa đơn (store có items) thì thu cột module
    // để cột hóa đơn được rộng như sidebar cũ (65vw) — cột module 496px + menu trái làm bảng dịch vụ bị dồn
    // trên màn ≤ 1600px. Người dùng bấm nút » ở header để mở lại khi cần thêm dịch vụ từ module.
    $ls_Items(items) {
      if (!this.isAutoFillFlow || this.hasAutoCollapsedModulePane || !items || items.length === 0) return;

      this.hasAutoCollapsedModulePane = true;
      if (this.$refs.moduleInvoice) {
        this.$refs.moduleInvoice.isOpen = false;
      }
    },
  },
}
